/**
 * Utility for handling chart print mode functionality
 * Provides a consistent way to manage chart rendering for print vs screen
 */

import { writable } from 'svelte/store';
import { theme } from '$stores/themeStore';
import { get } from 'svelte/store';

/**
 * Creates a chart print handler that manages print mode state and chart recreation
 * @param {Function} recreateChart - Function to recreate the chart
 * @param {Function} isChartReady - Function to check if chart is ready for recreation
 * @returns {Object} - Object with print mode store and cleanup function
 */
export function createChartPrintHandler(recreateChart, isChartReady = () => true) {
  const isPrintMode = writable(false);
  let cleanupFunctions = [];

  const handleBeforePrint = () => {
    if (isChartReady()) {
      isPrintMode.set(true);
      setTimeout(() => {
        recreateChart();
      }, 10);
    }
  };

  const handleAfterPrint = () => {
    if (isChartReady()) {
      isPrintMode.set(false);
      setTimeout(() => {
        recreateChart();
      }, 10);
    }
  };

  // Enhanced print preparation handler for manual print triggers
  const handlePrintPreparation = () => {
    if (isChartReady()) {
      isPrintMode.set(true);
      recreateChart();
    }
  };

  // Add event listeners
  window.addEventListener('beforeprint', handleBeforePrint);
  window.addEventListener('afterprint', handleAfterPrint);
  
  // Listen for custom print preparation event
  document.addEventListener('print-preparation', handlePrintPreparation);

  // Store cleanup functions
  cleanupFunctions.push(() => {
    window.removeEventListener('beforeprint', handleBeforePrint);
    window.removeEventListener('afterprint', handleAfterPrint);
    document.removeEventListener('print-preparation', handlePrintPreparation);
  });

  // Theme subscription for chart recreation
  const unsubscribeTheme = theme.subscribe(() => {
    if (isChartReady()) {
      recreateChart();
    }
  });
  cleanupFunctions.push(unsubscribeTheme);

  return {
    isPrintMode,
    cleanup: () => {
      cleanupFunctions.forEach(fn => fn());
      cleanupFunctions = [];
    }
  };
}

/**
 * Determines if light mode should be used based on theme and print state
 * @param {boolean} isPrintMode - Current print mode state
 * @returns {boolean} - True if light mode should be used
 */
export function shouldUseLightMode(isPrintMode) {
  const currentTheme = get(theme);
  return currentTheme === 'light' || isPrintMode;
}

/**
 * Gets appropriate text color based on theme and print state
 * @param {boolean} isPrintMode - Current print mode state
 * @returns {string} - CSS color value
 */
export function getTextColor(isPrintMode) {
  return shouldUseLightMode(isPrintMode) ? 'black' : 'white';
}

/**
 * Gets chart options based on theme and print state
 * @param {Object} lightOptions - Chart options for light mode
 * @param {Object} darkOptions - Chart options for dark mode
 * @param {boolean} isPrintMode - Current print mode state
 * @returns {Object} - Chart options to use
 */
export function getChartOptions(lightOptions, darkOptions, isPrintMode) {
  const baseOptions = shouldUseLightMode(isPrintMode) ? lightOptions : darkOptions;

  return {
    ...baseOptions,
    ...(isPrintMode ? { animation: false, animations: false } : {}),
  };
}

/**
 * Creates a dynamic chart options getter for TimeInRange-style charts
 * @param {Object} config - Configuration object
 * @param {boolean} isPrintMode - Current print mode state
 * @returns {Object} - Chart options
 */
export function getDynamicChartOptions(config, isPrintMode) {
  const isDark = !shouldUseLightMode(isPrintMode);
  const textColor = isDark ? '#FFFFFF' : '#000000';
  const tooltipBg = isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)';

  return {
    responsive: false,
    maintainAspectRatio: false,
    ...(isPrintMode ? { animation: false, animations: false } : {}),
    ...config.baseOptions,
    scales: {
      ...config.baseOptions?.scales,
      x: {
        ...config.baseOptions?.scales?.x,
        ticks: {
          ...config.baseOptions?.scales?.x?.ticks,
          color: textColor,
        }
      },
      y: {
        ...config.baseOptions?.scales?.y,
        ticks: {
          ...config.baseOptions?.scales?.y?.ticks,
          color: textColor,
        },
        title: {
          ...config.baseOptions?.scales?.y?.title,
          color: textColor,
        }
      }
    },
    plugins: {
      ...config.baseOptions?.plugins,
      legend: {
        ...config.baseOptions?.plugins?.legend,
        labels: {
          ...config.baseOptions?.plugins?.legend?.labels,
          color: textColor,
        }
      },
      tooltip: {
        ...config.baseOptions?.plugins?.tooltip,
        titleColor: textColor,
        bodyColor: textColor,
        backgroundColor: tooltipBg,
      },
      title: {
        ...config.baseOptions?.plugins?.title,
        color: textColor,
      }
    }
  };
}

/**
 * Creates a complete chart manager that handles both print mode and theme changes
 * This is a higher-level utility that combines print handling with chart lifecycle management
 * @param {Function} createChart - Function to create/recreate the chart
 * @param {Function} updateChart - Function to update chart data without recreation
 * @param {Function} isChartReady - Function to check if chart is ready
 * @returns {Object} - Object with chart manager functions
 */
export function createChartManager(createChart, updateChart = null, isChartReady = () => true) {
  let chart = null;
  let isInitialized = false;
  
  const printHandler = createChartPrintHandler(
    () => {
      if (isChartReady() && isInitialized) {
        createChart();
      }
    },
    () => isChartReady() && isInitialized
  );

  return {
    isPrintMode: printHandler.isPrintMode,
    setChart: (chartInstance) => { chart = chartInstance; },
    getChart: () => chart,
    initialize: () => { 
      isInitialized = true;
      createChart();
    },
    update: () => {
      if (updateChart && chart && isInitialized) {
        updateChart();
      } else if (chart && isInitialized) {
        createChart();
      }
    },
    cleanup: () => {
      printHandler.cleanup();
      if (chart) {
        chart.destroy();
        chart = null;
      }
      isInitialized = false;
    }
  };
}

/**
 * Helper to create print-aware canvas styles
 * @returns {string} - CSS classes for canvas elements
 */
export function getCanvasClasses() {
  return 'print:bg-white print:dark:bg-white';
}
